import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import type { ExecuteEntity, MaterialDestiny } from '@/types/run-stage.types';

interface ExecutionSummaryProps {
  execute: ExecuteEntity;
}

function formatDestiny(destiny?: MaterialDestiny | string): string {
  if (!destiny) return '—';
  if (typeof destiny === 'string') return destiny;
  switch (destiny.action) {
    case 'discard':
      return 'Discard';
    case 'return':
      return 'Return to Current Storage Location';
    case 'relocate': {
      const location = destiny.toLocation?.name ?? '—';
      const sub = destiny.subPosition ? ` · ${destiny.subPosition}` : '';
      return `Relocate to ${location}${sub}`;
    }
    default:
      return '—';
  }
}

export const ExecutionSummary: React.FC<ExecutionSummaryProps> = ({ execute }) => {
  const completedSteps = (execute.steps ?? []).filter(
    (s) => s.completed || s.status === 'COMPLETED'
  );
  const skippedSteps = (execute.steps ?? []).filter((s) => s.status === 'SKIPPED');
  const totalSteps = execute.steps?.length ?? 0;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Execution Summary
      </Typography>

      {/* Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {completedSteps.length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Completed
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {skippedSteps.length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Skipped
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {totalSteps - completedSteps.length - skippedSteps.length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Remaining
          </Typography>
        </Paper>
      </Stack>

      {/* Material cleanup table */}
      {execute.nodes && execute.nodes.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Material Cleanup
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Material / Container</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Booking Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Destiny</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {execute.nodes.map((node, idx) => (
                  <TableRow key={`${node.nodeId}-${node.cloneNumber ?? 0}-${idx}`} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {node.nodeName || node.materialName || node.containerName || node.nodeId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {node.material?.status === true || node.material?.status === 'true' ? (
                        <Chip label="Booked" color="success" size="small" variant="outlined" />
                      ) : (
                        <Chip label="Not Booked" color="default" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDestiny(node.material?.destiny)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Timing */}
      {(execute.startTime || execute.endTime) && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            Execution Timing
          </Typography>
          <Stack spacing={0.5}>
            {execute.startTime && (
              <Typography variant="body2" color="text.secondary">
                Started: {new Date(execute.startTime).toLocaleString()}
              </Typography>
            )}
            {execute.endTime && (
              <Typography variant="body2" color="text.secondary">
                Completed: {new Date(execute.endTime).toLocaleString()}
              </Typography>
            )}
            {execute.duration && (
              <Typography variant="body2" color="text.secondary">
                Duration: {execute.duration}
              </Typography>
            )}
          </Stack>
        </Paper>
      )}
    </Box>
  );
};
